import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Lazy-load Square Web Payments SDK and initialize a card element.
// Reads the SDK URL + application/location IDs from the backend so we can
// swap between sandbox and production purely via backend env vars.
export function useSquareCard(containerId) {
  const [card, setCard] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const { data: config } = await api.get("/square/config");
        if (!config.application_id || !config.location_id) {
          throw new Error("Square is not configured");
        }
        // Inject Square script if not already loaded
        if (!window.Square) {
          await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = config.sdk_url;
            s.async = true;
            s.onload = resolve;
            s.onerror = () => reject(new Error("Failed to load Square SDK"));
            document.head.appendChild(s);
          });
        }
        if (cancelled) return;
        const payments = window.Square.payments(config.application_id, config.location_id);
        const c = await payments.card();
        await c.attach(`#${containerId}`);
        if (cancelled) {
          c.destroy();
          return;
        }
        cardRef.current = c;
        setCard(c);
        setReady(true);
      } catch (e) {
        setError(e.message || String(e));
      }
    };
    init();

    return () => {
      cancelled = true;
      if (cardRef.current) {
        try {
          cardRef.current.destroy();
        } catch {}
        cardRef.current = null;
      }
    };
  }, [containerId]);

  const tokenize = async () => {
    if (!cardRef.current) throw new Error("Card not ready");
    const result = await cardRef.current.tokenize();
    if (result.status !== "OK") {
      const msg = (result.errors || []).map((e) => e.message).join(", ") || "Card tokenization failed";
      throw new Error(msg);
    }
    return result.token;
  };

  return { card, ready, error, tokenize };
}
